import { C } from '../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../lib/ui';
import { useI18n } from '../../../../i18n';
import { Section, EmptyState, InlineEdit, InlineSelect, AddItemRow } from './ProcessSheetHelpers';
import type { IndicadorItem, FichaEditHook } from './ProcessSheetHelpers';

interface IndicadorReadonly {
  id: string;
  nombre: string;
  descripcion?: string | null;
  unidad?: string | null;
  valorObjetivo?: number | null;
}

interface ProcessSheetIndicatorsProps {
  indicadoresHook: FichaEditHook<IndicadorItem>;
  indicadoresReadonly: IndicadorReadonly[];
  editMode: boolean;
  addingSection: string | null;
  setAddingSection: (v: string | null) => void;
  newItemValue: string;
  setNewItemValue: (v: string) => void;
  onAdd: (
    section: string,
    hook: { crear: (d: Record<string, unknown>) => Promise<unknown> },
  ) => void;
}

export function ProcessSheetIndicators({
  indicadoresHook,
  indicadoresReadonly,
  editMode,
  addingSection,
  setAddingSection,
  newItemValue,
  setNewItemValue,
  onAdd,
}: ProcessSheetIndicatorsProps) {
  const { t } = useI18n();

  return (
    <Section
      icon={<Ic d={ICON_PATHS.chart} s={16} />}
      title={`7. ${t.processSheet.sections.indicators}`}
      fullWidth
    >
      {indicadoresHook.items.length === 0 && !indicadoresHook.loading ? (
        indicadoresReadonly.length === 0 ? (
          <EmptyState message={t.processSheet.noData} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {indicadoresReadonly.map((ind) => (
              <div key={ind.id}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{ind.nombre}</span>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: C.textMut }}>
                      {t.processSheet.fields.objective}:{' '}
                      {ind.valorObjetivo != null
                        ? `${ind.valorObjetivo}${ind.unidad ? ' ' + ind.unidad : ''}`
                        : '--'}
                    </span>
                  </div>
                </div>
                {ind.descripcion && (
                  <div style={{ fontSize: 12, color: C.textMut, marginTop: 2 }}>
                    {ind.descripcion}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {indicadoresHook.items.map((ind) => (
            <div
              key={ind.id}
              style={{
                padding: '8px 0',
                borderBottom: `1px solid ${C.borderAlt}`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ flex: 1 }}>
                  <InlineEdit
                    value={ind.nombre}
                    onSave={(v) => indicadoresHook.actualizar(ind.id, { nombre: v })}
                    editMode={editMode}
                    style={{ fontSize: 14, fontWeight: 600 }}
                  />
                  {!editMode && ind.descripcion && (
                    <div style={{ fontSize: 12, color: C.textMut, marginTop: 2 }}>
                      {ind.descripcion}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!editMode && (
                    <span style={{ fontSize: 12, color: C.textMut }}>
                      {t.processSheet.fields.objective}:{' '}
                      {ind.valorObjetivo != null
                        ? `${ind.valorObjetivo}${ind.unidad ? ' ' + ind.unidad : ''}`
                        : '--'}
                    </span>
                  )}
                  <InlineSelect
                    value={ind.tipoCalculo}
                    options={[
                      { value: 'MANUAL', label: 'Manual' },
                      { value: 'AUTO_CONTEO', label: 'Auto Conteo' },
                      { value: 'AUTO_PROMEDIO', label: 'Auto Promedio' },
                      { value: 'AUTO_PORCENTAJE', label: 'Auto %' },
                    ]}
                    onSave={(v) =>
                      indicadoresHook.actualizar(ind.id, { tipoCalculo: v || 'MANUAL' })
                    }
                    editMode={editMode}
                  />
                  {editMode && (
                    <button
                      onClick={() => indicadoresHook.eliminar(ind.id)}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 2,
                      }}
                    >
                      x
                    </button>
                  )}
                </div>
              </div>
              {editMode && (
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <InlineEdit
                    value={ind.descripcion ?? ''}
                    onSave={(v) => indicadoresHook.actualizar(ind.id, { descripcion: v || null })}
                    editMode={editMode}
                    placeholder={t.processSheet.placeholders.descripcion}
                    style={{ fontSize: 11, color: C.textMut, flex: 2 }}
                  />
                  <InlineEdit
                    value={ind.unidad ?? ''}
                    onSave={(v) => indicadoresHook.actualizar(ind.id, { unidad: v || null })}
                    editMode={editMode}
                    placeholder={t.processSheet.placeholders.unidad}
                    style={{ fontSize: 11, color: C.textMut, flex: 1 }}
                  />
                  <InlineEdit
                    value={ind.valorObjetivo != null ? String(ind.valorObjetivo) : ''}
                    onSave={(v) =>
                      indicadoresHook.actualizar(ind.id, {
                        valorObjetivo: v ? Number(v) : null,
                      })
                    }
                    editMode={editMode}
                    placeholder={t.processSheet.placeholders.objetivo}
                    type="number"
                    style={{ fontSize: 11, color: C.textMut, flex: 1 }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {editMode && (
        <AddItemRow
          sectionKey="indicadores"
          addingSection={addingSection}
          setAddingSection={setAddingSection}
          newItemValue={newItemValue}
          setNewItemValue={setNewItemValue}
          onAdd={() => onAdd('indicadores', indicadoresHook)}
          addLabel={t.processSheet.addItem}
        />
      )}
    </Section>
  );
}
